'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Header } from '~/app/_components/Header'
import { Footer } from '~/app/_components/Footer'
import tigoLogoSage from 'public/images/logos/tigo/tigo-icon-sage.png'
import tigoLogoTerracotta from 'public/images/logos/tigo/tigo-icon-terracotta .png'
import tigoLogoWhite from 'public/images/logos/tigo/tigo-icon-wite.png'

type BackgroundColor = 'cream' | 'black' | 'transparent' | 'primary' | 'secondary' | 'accent'

interface BackgroundOption {
  id: BackgroundColor
  name: string
  color: string
  isTransparent?: boolean
}

interface ColorItem {
  name: string
  variable: string
  hex: string
  description?: string
}

interface FontItem {
  name: string
  variable: string
  cssVariable: string
  family: string
}

const colors: ColorItem[] = [
  { name: 'Primary (Sage)', variable: '--color-primary', hex: '#8E9C87', description: 'Main brand color - Sage' },
  { name: 'Primary Dark (Sage Dark)', variable: '--color-primary-dark', hex: '#6B7765', description: 'Darker variant of primary - Sage Dark' },
  { name: 'Secondary (Terracotta)', variable: '--color-secondary', hex: '#C67452', description: 'Secondary brand color - Terracotta' },
  { name: 'Secondary Dark (Terracotta Dark)', variable: '--color-secondary-dark', hex: '#A05A3F', description: 'Darker variant of secondary - Terracotta Dark' },
  { name: 'Accent (Coral Pink)', variable: '--color-accent', hex: '#F28C82', description: 'Accent color - Coral Pink' },
  { name: 'Accent Dark (Coral Pink Dark)', variable: '--color-accent-dark', hex: '#D96F65', description: 'Darker variant of accent - Coral Pink Dark' },
  { name: 'Cream', variable: '--color-cream', hex: '#F4F0E6', description: 'Light background color' },
]

const fonts: FontItem[] = [
  { 
    name: 'Sans (Inter)', 
    variable: '--font-inter', 
    cssVariable: '--font-sans',
    family: 'Inter, sans-serif'
  },
  { 
    name: 'Display (Lexend)', 
    variable: '--font-lexend', 
    cssVariable: '--font-display',
    family: 'Lexend, sans-serif'
  },
]

const backgroundOptions: BackgroundOption[] = [
  { id: 'cream', name: 'Cream', color: '#F4F0E6' },
  { id: 'black', name: 'Black', color: '#000000' },
  { id: 'transparent', name: 'Transparent', color: 'transparent', isTransparent: true },
  { id: 'primary', name: 'Primary (Sage)', color: '#8E9C87' },
  { id: 'secondary', name: 'Secondary (Terracotta)', color: '#C67452' },
  { id: 'accent', name: 'Accent (Coral Pink)', color: '#F28C82' },
]

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-300"
      aria-label={`Copy ${label}`}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  )
}

function ColorCard({ color }: { color: ColorItem }) {
  // Extract human name if it exists (e.g., "Primary (Sage)" -> "Sage")
  const regex = /\(([^)]+)\)/
  const match = regex.exec(color.name)
  const humanName = color.name.includes('(') && match 
    ? match[1] 
    : null
  
  // Map to legacy variable names based on CSS definitions
  const legacyVariableMap: Record<string, string> = {
    '--color-primary': '--color-sage',
    '--color-primary-dark': '--color-sage-dark',
    '--color-secondary': '--color-terracotta',
    '--color-secondary-dark': '--color-terracotta-dark',
    '--color-accent': '--color-coral-pink',
    '--color-accent-dark': '--color-coral-pink-dark',
  }
  
  const legacyVariable = legacyVariableMap[color.variable] ?? null

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{color.name}</h3>
          {color.description && (
            <p className="text-sm text-gray-500 mt-1">{color.description}</p>
          )}
        </div>
        <div className="w-16 h-16 rounded-lg border-2 border-gray-200 shadow-sm" style={{ backgroundColor: color.hex }} />
      </div>
      
      <div className="space-y-2">
        {humanName && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Human Name:</span>
            <div className="flex items-center gap-2">
              <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
                {humanName}
              </code>
              <CopyButton text={humanName} label={`human name ${color.name}`} />
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Variable:</span>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
              {color.variable}
            </code>
            <CopyButton text={color.variable} label={`variable ${color.name}`} />
          </div>
        </div>
        
        {legacyVariable && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Legacy Variable:</span>
            <div className="flex items-center gap-2">
              <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
                {legacyVariable}
              </code>
              <CopyButton text={legacyVariable} label={`legacy variable ${color.name}`} />
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Hex:</span>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
              {color.hex}
            </code>
            <CopyButton text={color.hex} label={`hex ${color.name}`} />
          </div>
        </div>
      </div>
    </div>
  )
}

function LogoVariantCard({ 
  name, 
  image, 
  path,
  defaultBg = 'cream'
}: { 
  name: string
  image: typeof tigoLogoSage
  path: string
  defaultBg?: BackgroundColor
}) {
  const [selectedBg, setSelectedBg] = useState<BackgroundColor>(defaultBg)

  const getBackgroundStyle = () => {
    const option = backgroundOptions.find(opt => opt.id === selectedBg)
    if (!option) return { backgroundColor: '#F4F0E6' }
    
    if (option.isTransparent) {
      return {
        backgroundImage: `
          linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
          linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
          linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
      }
    }
    
    return { backgroundColor: option.color }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{name}</h3>
      
      {/* Background Color Picker */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">Background:</span>
          <div className="flex items-center gap-1 border border-gray-300 rounded p-0.5 bg-gray-50">
            {backgroundOptions.map((option) => {
              const isSelected = selectedBg === option.id
              const isTransparent = option.isTransparent
              
              return (
                <button
                  key={option.id}
                  onClick={() => setSelectedBg(option.id)}
                  className={`w-4 h-4 rounded-sm border-2 transition-all relative ${
                    isSelected
                      ? 'border-gray-900 shadow-sm scale-110 z-10'
                      : 'border-gray-400 hover:border-gray-600 hover:scale-105'
                  }`}
                  style={
                    isTransparent
                      ? {
                          backgroundImage: `
                            linear-gradient(45deg, #d1d5db 25%, transparent 25%),
                            linear-gradient(-45deg, #d1d5db 25%, transparent 25%),
                            linear-gradient(45deg, transparent 75%, #d1d5db 75%),
                            linear-gradient(-45deg, transparent 75%, #d1d5db 75%)
                          `,
                          backgroundSize: '4px 4px',
                          backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px',
                        }
                      : { backgroundColor: option.color }
                  }
                  title={option.name}
                  aria-label={option.name}
                >
                  {isSelected && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full shadow-sm" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Logo Preview */}
      <div 
        className="p-6 rounded-lg border-2 border-gray-200 mb-4 flex items-center justify-center min-h-[200px]"
        style={getBackgroundStyle()}
      >
        <Image 
          src={image} 
          alt={`Tigo Logo - ${name}`} 
          width={200}
          height={200}
          className="object-contain"
        />
      </div>

      {/* Path */}
      <div className="text-center">
        <p className="text-sm text-gray-600 mb-2">Path:</p>
        <div className="flex items-center justify-center gap-2">
          <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
            {path}
          </code>
          <CopyButton text={path} label={`${name.toLowerCase()} logo path`} />
        </div>
      </div>
    </div>
  )
}

function FontCard({ font }: { font: FontItem }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{font.name}</h3>
        <div 
          className="text-4xl font-bold mb-4"
          style={{ fontFamily: font.family }}
        >
          Aa
        </div>
        <p 
          className="text-lg mb-2"
          style={{ fontFamily: font.family }}
        >
          The quick brown fox jumps over the lazy dog
        </p>
        <p 
          className="text-sm text-gray-600"
          style={{ fontFamily: font.family }}
        >
          0123456789 !@#$%^&*()
        </p>
      </div>
      
      <div className="space-y-2 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">CSS Variable:</span>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
              {font.cssVariable}
            </code>
            <CopyButton text={font.cssVariable} label={`CSS variable ${font.name}`} />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Font Variable:</span>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
              {font.variable}
            </code>
            <CopyButton text={font.variable} label={`font variable ${font.name}`} />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Font Family:</span>
          <div className="flex items-center gap-2">
            <code className="text-sm bg-gray-50 px-2 py-1 rounded border border-gray-200 font-mono">
              {font.family}
            </code>
            <CopyButton text={font.family} label={`font family ${font.name}`} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function BrandingPage() {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Brand Guidelines</h1>
          <p className="text-lg text-gray-600">
            Reference for colors, fonts, and logo usage
          </p>
        </div>

        {/* Logo Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Logo Variants</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <LogoVariantCard
              name="Sage"
              image={tigoLogoSage}
              path="/images/logos/tigo/tigo-icon-sage.png"
            />
            <LogoVariantCard
              name="Terracotta"
              image={tigoLogoTerracotta}
              path="/images/logos/tigo/tigo-icon-terracotta .png"
            />
            <LogoVariantCard
              name="White"
              image={tigoLogoWhite}
              path="/images/logos/tigo/tigo-icon-wite.png"
              defaultBg="primary"
            />
          </div>
        </section>

        {/* Colors Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Color Palette</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colors.map((color) => (
              <ColorCard key={color.variable} color={color} />
            ))}
          </div>
        </section>

        {/* Fonts Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Typography</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fonts.map((font) => (
              <FontCard key={font.variable} font={font} />
            ))}
          </div>
        </section>
        </div>
      </div>
      <Footer />
    </>
  )
}
